import React, { useState } from 'react';
import { FormContext, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import { useFetchWithStatusV2 } from 'hooks/fetchWithStatus';
import { useUserContext } from 'providers/UserProvider';
import { EntityRoutes } from 'routes/constants';
import { userHasSomeRoles } from 'services/Authorization';
import strings from 'strings';
import { When } from 'UI/components/atoms';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import HighlightBox from 'UI/components/molecules/HighlightBox';
import DecisionDialog from 'UI/components/organisms/DecisionDialog';
import EmptyPlaceholder from 'UI/components/templates/EmptyPlaceholder';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import { Endpoints } from 'UI/constants/endpoints';
import { CompanyEntity, JobOrderEntity } from 'UI/constants/entityTypes';
import { Roles } from 'UI/constants/roles';
import { UIStatus } from 'UI/constants/status';

import { JobOrderDetails } from '../../shared';

import { ATTACHMENT_FIELD_KEYS } from './SendoutForm/SendoutForm.utils';
import { JOB_ORDER_INCLUDES } from './CreateSendoutDrawer.constants';
import { LoaderContainer } from './CreateSendoutDrawer.styled';
import { buildSendoutSubmissionData } from './CreateSendoutDrawer.utils';
import { SendoutForm } from './SendoutForm';

const CONFIRMATION_DIALOGS = {
  NoAttachments: 'noAttachments',
  Preview: 'preview'
};

export const CreateSendoutDrawer = ({
  accountableId = null,
  collaboratorId = null,
  jobOrderId = null,
  company = {},
  onClose = () => {},
  onSaveAsDraft = () => {},
  onSaved = () => {},
  onViewDuplicates = () => {}
}) => {
  const [user] = useUserContext();
  const history = useHistory();
  const form = useForm();

  const [candidateDuplicateInfo, setCandidateDuplicateInfo] = useState({
    isDuplicate: false,
    duplicates: [],
    error: null
  });
  const [candidateInfo, setCandidateInfo] = useState(null);
  const [submissionData, setSubmissionData] = useState(null);
  const [activeDialog, setActiveDialog] = useState(null);

  const isAccountable = user?.id === accountableId;
  const isCollaborator = user?.id === collaboratorId;

  const hasSuperiorRole = userHasSomeRoles(user, [
    Roles.AssistantRegionalDirector,
    Roles.Coach,
    Roles.Operations,
    Roles.ProductionDirector,
    Roles.Leadership,
    Roles.DataCoordinator
  ]);

  const isCompanySignedOrClient = company.isSignedOrClient;

  const isCreationEnabled =
    (isAccountable || isCollaborator || hasSuperiorRole) && isCompanySignedOrClient;

  const { state } = useFetchWithStatusV2({
    endpoint: isCreationEnabled
      ? `${Endpoints.JobOrders}/${jobOrderId}?includes=${JOB_ORDER_INCLUDES}`
      : null,
    apiVersion: 2
  });

  const { handleSubmit } = form;

  const isFetching = isCreationEnabled && state.status === UIStatus.Loading;

  const jobOrder = state.results || {};

  const sendoutInfo = {
    jobOrder: {
      id: jobOrder?.id,
      title: JobOrderEntity.singular,
      name: jobOrder?.title
    },
    company: {
      id: jobOrder?.company?.id,
      title: CompanyEntity.singular,
      name: jobOrder?.company?.name
    }
  };

  const submitSendout = snapshot => {
    console.log({ snapshot }); // TODO: replace with real submit logic
    onSaved({ closeAll: true });
  };

  const closeConfirmationFlow = () => {
    setActiveDialog(null);
    setSubmissionData(null);
  };

  const getSelectedAttachments = formData =>
    ATTACHMENT_FIELD_KEYS.flatMap(fieldKey => formData?.[fieldKey] || []);

  const handleSave = formData => {
    const nextSubmissionData = buildSendoutSubmissionData({
      formData,
      jobOrder,
      candidateInfo
    });
    const selectedAttachments = getSelectedAttachments(formData);
    const hasNoAttachments = selectedAttachments.length === 0;

    setSubmissionData(nextSubmissionData);

    if (hasNoAttachments) {
      setActiveDialog(CONFIRMATION_DIALOGS.NoAttachments);
      return;
    }

    setActiveDialog(CONFIRMATION_DIALOGS.Preview);
  };

  const handleConfirmNoAttachments = () => {
    setActiveDialog(CONFIRMATION_DIALOGS.Preview);
  };

  const handleConfirmPreview = () => {
    if (!submissionData) return;

    submitSendout(submissionData);
    closeConfirmationFlow();
  };

  const handleViewCompanyProfile = () => {
    if (!company?.id) return;

    history.push(EntityRoutes.CompanyProfile.replace(':id', company.id));
  };

  const buttonDefinitions = [
    {
      props: {
        text: strings.shared.ui.cancel,
        variant: 'outlined',
        onClick: onClose
      },
      GridItemProps: { md: 3 }
    },
    {
      props: {
        text: strings.shared.ui.saveAsDraft,
        variant: 'outlined',
        onClick: onSaveAsDraft,
        disabled: isFetching
      },
      GridItemProps: { md: 3 },
      visible: isCreationEnabled
    },
    {
      props: {
        text: strings.shared.ui.save,
        variant: 'contained',
        onClick: handleSubmit(handleSave),
        disabled: isFetching || candidateDuplicateInfo.isDuplicate
      },
      GridItemProps: { md: 3 },
      mode: 'save',
      visible: isCreationEnabled
    }
  ];

  return (
    <>
      <FPDrawer
        FPDrawerProps={{ open: true }}
        CardHeaderProps={{ title: strings.sendouts.tab.actions.createSendout }}
        onClose={onClose}
        footer={
          <FPButtonBuilder
            GridContainerProps={{ spacing: 2 }}
            buttonDefinitions={buttonDefinitions}
          />
        }
      >
        <When condition={!isCreationEnabled}>
          <EmptyPlaceholder
            subtitle={strings.formatString(strings.sendoutsV2.form.emptyPlaceholderText, {
              documentType: 'Sendout'
            })}
            height="100%"
          />
        </When>
        <When condition={isFetching}>
          <LoaderContainer>
            <CircularProgress />
          </LoaderContainer>
        </When>
        <When condition={isCreationEnabled && state.status === UIStatus.Success}>
          <FormContext {...form}>
            <JobOrderDetails company={sendoutInfo.company} jobOrder={sendoutInfo.jobOrder} />
            <SendoutForm
              candidateInfo={candidateInfo}
              jobOrder={jobOrder}
              onCandidateInfoChange={setCandidateInfo}
              onViewDuplicates={onViewDuplicates}
              onDuplicateChange={setCandidateDuplicateInfo}
            />
          </FormContext>
        </When>
      </FPDrawer>
      <When condition={!isCompanySignedOrClient}>
        <DecisionDialog
          cancelButtonText={strings.sendoutsV2.alerts.noSignedCompany.cancel}
          confirmButtonText={strings.sendoutsV2.alerts.noSignedCompany.viewCompany}
          isOpened
          onClose={onClose}
          onConfirm={handleViewCompanyProfile}
          severity="info"
          showCloseButton={false}
          showSeverityIcon={false}
          title={strings.sendoutsV2.alerts.noSignedCompany.title}
          withButtons="YesNo"
        >
          <Typography>{strings.sendoutsV2.alerts.noSignedCompany.description}</Typography>
        </DecisionDialog>
      </When>
      <When condition={activeDialog === CONFIRMATION_DIALOGS.NoAttachments}>
        <DecisionDialog
          cancelButtonText={strings.shared.ui.cancel}
          confirmButtonText={strings.shared.ui.send}
          isOpened
          onClose={closeConfirmationFlow}
          onConfirm={handleConfirmNoAttachments}
          severity="warning"
          showCloseButton={false}
          showSeverityIcon
          title={strings.sendoutsV2.alerts.confirmationDialogs.noAttachments.title}
          withButtons="YesNo"
          message={strings.sendoutsV2.alerts.confirmationDialogs.noAttachments.message}
        />
      </When>
      <When condition={activeDialog === CONFIRMATION_DIALOGS.Preview}>
        <DecisionDialog
          cancelButtonText={strings.shared.ui.cancel}
          confirmButtonText={strings.shared.ui.singleConfirm}
          isOpened
          onClose={closeConfirmationFlow}
          onConfirm={handleConfirmPreview}
          severity="info"
          showCloseButton={false}
          showSeverityIcon
          title={strings.sendoutsV2.alerts.confirmationDialogs.emailConfirmation.title}
          message={strings.sendoutsV2.alerts.confirmationDialogs.emailConfirmation.message}
          withButtons="YesNo"
        >
          <HighlightBox
            text={submissionData?.confirmation?.resolvedEmailBody || ''}
            highlightData={submissionData?.confirmation?.resolvedTagValues || []}
          />
        </DecisionDialog>
      </When>
    </>
  );
};
